import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { Drawer, drawerType } from '../../../../packages/ui/organisms/drawer';
import { Input, inputSize, inputType } from '../../../../packages/ui/atoms/input';
import { Textarea } from '../../../../packages/ui/atoms/textarea';
import { Text, textLevel, fontWeight, colors, TextError } from '../../../../packages';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../redux/modules/auth/selectors';
import { profileActionCreators } from '../../../../redux/modules/profile';
import { searchExternalMedications } from '../../../../services/schedule/schedule.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';
import debounce from 'debounce';

const generateExternalRxId = () => `ext_${crypto.randomUUID()}`;

const AddExternalRxDrawer = ({ open, onClose, memberId, otherDomainElementId, existingRecord = null }) => {
  const dispatch = useDispatch();
  const { profile } = useSelector(getProfile);
  const { meta } = useSelector(getAuth);
  const [isSaving, setIsSaving] = useState(false);
  const [externalMedications, setExternalMedications] = useState([]);
  const [formData, setFormData] = useState({
    drugName: '',
    drugInfo: '',
    providerName: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Check if we're in edit mode
  const isEditMode = !!existingRecord;

  // Pre-populate form when editing
  useEffect(() => {
    if (existingRecord && open) {
      const rxDrugInfo = existingRecord.tagMetaData?.rxDrugInfo || {};
      setFormData({
        drugName: existingRecord.note || rxDrugInfo.additionalNotes || '',
        drugInfo: rxDrugInfo.sig || '',
        providerName: rxDrugInfo.providerName || '',
      });
      setErrors({});
      setTouched({});
    } else if (!existingRecord && open) {
      // Reset form for new entry
      setFormData({
        drugName: '',
        drugInfo: '',
        providerName: '',
      });
      setErrors({});
      setTouched({});
    }
  }, [existingRecord, open]);

  // Prevent space key from triggering navigation when drawer is open and user is typing
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = event => {
      // Check if space key is pressed and focus is on an input/textarea/autocomplete
      if (event.key === ' ' || event.keyCode === 32) {
        const activeElement = document.activeElement;
        const isInputField =
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          activeElement?.getAttribute('role') === 'combobox' ||
          activeElement?.getAttribute('aria-autocomplete') !== null;

        if (isInputField) {
          event.stopPropagation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  const searchForExternalMedications = async searchQuery => {
    try {
      const response = await searchExternalMedications(searchQuery, '');
      if (response.data) {
        setExternalMedications(response.data);
      }
    } catch (error) {
      console.error('Error searching external medications:', error);
    }
  };

  const debounceSearch = async searchQuery => {
    void searchForExternalMedications(searchQuery);
  };

  const drugSearchOptions = externalMedications.map(med => ({
    label: med.drug,
    value: med.id, // This id should be used as domainElementId when selected
  }));

  const searchDelayed = useMemo(() => debounce(debounceSearch, 1000), []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field] && errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = field => {
    if (field === 'drugName') {
      const drugNameValue =
        typeof formData.drugName === 'string'
          ? formData.drugName.trim()
          : formData.drugName?.label || formData.drugName || '';
      if (!drugNameValue) {
        setErrors(prev => ({ ...prev, [field]: 'Required' }));
        return false;
      } else {
        // Clear error if value exists
        setErrors(prev => ({ ...prev, [field]: null }));
      }
    }
    return true;
  };

  const validateForm = () => {
    const newErrors = {};
    const drugNameValue =
      typeof formData.drugName === 'string' ? formData.drugName.trim() : formData.drugName?.label || '';
    if (!drugNameValue) {
      newErrors.drugName = 'Required';
    }
    setErrors(newErrors);
    setTouched({
      drugName: true,
      drugInfo: true,
      providerName: true,
    });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setIsSaving(true);

    // Get drug name - either from selected option or free text
    const drugName =
      typeof formData.drugName === 'string'
        ? formData.drugName.trim()
        : formData.drugName?.label || formData.drugName || '';

    // For external RX, always use "Other" as the domainElementId
    const domainElementId = otherDomainElementId;

    // Use existing pharmacyNotes ID when editing, or generate new one for new entries
    const externalRxUniqueId = isEditMode
      ? existingRecord?.tagMetaData?.rxDrugInfo?.pharmacyNotes || generateExternalRxId()
      : generateExternalRxId();

    const data = {
      patientId: memberId,
      tagItems: [
        {
          assignedBy: profile.fullName || meta.nickName,
          domainElementId,
          metaData: {
            notes: drugName, // Drug name goes in notes
            selectedImportance: 'ACTIVE_EXTERNAL_RX',
            rxDrugInfo: {
              sig: formData.drugInfo || '', // Drug information goes in sig
              additionalNotes: drugName,
              providerName: formData.providerName || '', // Name of external prescriber
              prescribingProvider: 'External',
              pharmacyNotes: externalRxUniqueId, // Unique ID for grouping external RX records
            },
          },
        },
      ],
    };

    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: (errorMsg, response) => {
          setIsSaving(false);
          if (errorMsg) {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: errorMsg || 'Failed to record external RX. Please try again.',
              }),
            );
            handleClose(false);
            return;
          }

          dispatch(
            showSnackbar({
              snackType: 'success',
              snackMessage: isEditMode
                ? 'External RX updated successfully'
                : 'External RX recorded successfully',
            }),
          );

          // Use try-catch to prevent errors from propagating back to saga
          try {
            handleClose(true);
          } catch (closeError) {
            console.error('Error closing drawer:', closeError);
          }
        },
      }),
    );
  };

  const handleClose = (shouldRefresh = false) => {
    if (isSaving) return;
    setFormData({
      drugName: '',
      drugInfo: '',
      providerName: '',
    });
    setErrors({});
    setTouched({});
    setExternalMedications([]);
    onClose(shouldRefresh); // Pass shouldRefresh to parent
  };

  useEffect(() => {
    if (!open) {
      // Reset form when drawer closes
      setFormData({
        drugName: '',
        drugInfo: '',
        providerName: '',
      });
      setErrors({});
      setTouched({});
      setExternalMedications([]);
    }
  }, [open]);

  // Validate drug name - can be a string (free text) or an object with label
  const isDrugNameValid =
    formData.drugName &&
    (typeof formData.drugName === 'string'
      ? formData.drugName.trim()
      : formData.drugName?.label || formData.drugName);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      variant={drawerType.FORM}
      title={isEditMode ? 'Update External RX' : 'External RX'}
      submitBtnTitle={isEditMode ? 'Update' : 'Continue'}
      onSubmit={handleSubmit}
      disableSubmit={isSaving || !isDrugNameValid}
    >
      <Box display="flex" flexDirection="column" gap="24px">
        <Box display="flex" flexDirection="column" gap="8px">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.neutral900}>
              Drug Name
            </Text>
            {touched.drugName && errors.drugName && <TextError errorMsg={errors.drugName} />}
          </Box>
          <Input
            fullWidth
            variant={inputType.AUTOCOMPLETE}
            filterSelectedOptions={false}
            freeSolo
            options={drugSearchOptions}
            getOptionLabel={option => {
              if (typeof option === 'string') return option;
              return option?.label ?? option?.title ?? option ?? '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'string' || typeof value === 'string') {
                return option === value;
              }
              return option?.value === value?.value || option?.label === value?.label;
            }}
            value={formData.drugName}
            size={inputSize.S}
            placeholder="Search for a medication"
            onChange={(event, value) => {
              handleInputChange('drugName', value);
            }}
            onInputChange={(event, newInputValue) => {
              if (!event) return;
              if (event.code === 'Enter' || event.code === 'NumpadEnter' || event.type === 'click') {
                return;
              }
              // Update formData with free text input value
              if (newInputValue && typeof newInputValue === 'string') {
                handleInputChange('drugName', newInputValue);
              }
              if (newInputValue) {
                searchDelayed(newInputValue);
              }
            }}
            onBlur={event => {
              // On blur, capture the current input value if it's free text
              const currentValue = event.target.value;
              if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
                // If we have free text, update formData with it
                if (
                  !formData.drugName ||
                  (typeof formData.drugName === 'string' && formData.drugName !== currentValue)
                ) {
                  handleInputChange('drugName', currentValue);
                }
              }
              handleBlur('drugName');
            }}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap="8px">
          <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.neutral900}>
            Drug Information
          </Text>
          <Textarea
            fullWidth
            name="drugInfo"
            value={formData.drugInfo}
            placeholder="Record dosage, frequency, and other sig information"
            onChange={event => handleInputChange('drugInfo', event.target.value)}
            onBlur={() => handleBlur('drugInfo')}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap="8px">
          <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.neutral900}>
            Name of External Prescriber
          </Text>
          <Input
            fullWidth
            name="providerName"
            value={formData.providerName}
            placeholder="Enter prescriber name"
            onChange={event => handleInputChange('providerName', event.target.value)}
            onBlur={() => handleBlur('providerName')}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddExternalRxDrawer;
